import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    CircularProgress,
    Typography,
    TextField,
    InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
    DataGrid,
    GridColDef,
    GridSortModel,
    GridFilterModel,
    GridPaginationModel,
    GridLoadingOverlayProps
} from '@mui/x-data-grid';
import api from '../../../services/api';

interface RedemptionTableProps {
    couponId: string;
    searchQuery: string;
}

interface Redemption {
    _id: string;
    firstName: string;
    lastName: string;
    customerEmail: string;
    redeemedAt: string;
}

const CustomLoadingOverlay = (props: GridLoadingOverlayProps) => (
    <Box
        sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.5)'
        }}
    >
        <CircularProgress />
    </Box>
);

const RedemptionTable: React.FC<RedemptionTableProps> = ({ couponId, searchQuery }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<Redemption[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        page: 0,
        pageSize: 10,
    });
    const [sortModel, setSortModel] = useState<GridSortModel>([
        { field: 'redeemedAt', sort: 'desc' }
    ]);

    const columns: GridColDef[] = [
        {
            field: 'firstName',
            headerName: 'First Name',
            flex: 1,
            minWidth: 130
        },
        {
            field: 'lastName',
            headerName: 'Last Name',
            flex: 1,
            minWidth: 130
        },
        {
            field: 'customerEmail',
            headerName: 'Email',
            flex: 1,
            minWidth: 200
        },
        {
            field: 'redeemedAt',
            headerName: 'Redeemed At',
            flex: 1,
            minWidth: 180,
            renderCell: (params: { row: Redemption }) => {
                const date = params.row.redeemedAt;
                if (!date) return '';
                
                try {
                    return new Date(date).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true,
                        timeZone: 'America/New_York'
                    });
                } catch (err) {
                    console.error('Date parsing error:', err);
                    return 'Invalid Date';
                }
            }
        }
    ];

    const fetchRedemptions = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/admin/coupons/${couponId}/redemptions`, {
                params: {
                    page: paginationModel.page + 1,
                    limit: paginationModel.pageSize,
                    sort: sortModel[0]?.field || 'redeemedAt',
                    order: sortModel[0]?.sort || 'desc',
                    search: searchQuery
                }
            });

            const { redemptions, pagination } = response.data.data;
            setData(redemptions);
            setTotalRows(pagination.total);
            setError(null);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load redemptions');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRedemptions();
    }, [couponId, paginationModel, sortModel, searchQuery]);

    if (error) {
        return (
            <Box p={3}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Paper sx={{ height: 600, width: '100%' }}>
            <DataGrid
                rows={data}
                columns={columns}
                getRowId={(row: Redemption) => row._id}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                loading={loading}
                rowCount={totalRows}
                pageSizeOptions={[10, 25, 50]}
                paginationMode="server"
                sortingMode="server"
                filterMode="server"
                disableColumnFilter
                disableDensitySelector
                slots={{
                    loadingOverlay: CustomLoadingOverlay
                }}
            />
        </Paper>
    );
};

export default RedemptionTable;
